import { Dropdown, Input, Text } from '@ui/themed';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { useSettings } from '../../../context/settings-context';
import { useShippingMethodForm } from '../builder-context';
import { type ShippingBandBuilderState, shippingBandRequirementSchema } from '../schemas';
import type { ShippingBandApiResponse } from '../types/shipping-method';

function getDefaultRequirementState(
  requirementType: ShippingBandApiResponse['requirement']['type'],
): ShippingBandBuilderState['requirement'] {
  switch (requirementType) {
    case 'none':
      return { type: requirementType };
    case 'net_total':
      return { type: requirementType, minTotal: '', maxTotal: '' };
    case 'weight':
      return { type: requirementType, minGrams: '', maxGrams: '' };
  }

  throw new Error('Unhandled requirement type');
}

// Each requirement type has different properties associated with it, so will show different form fields
export function BandRequirement({
  band,
  index,
}: {
  band: ShippingBandBuilderState;
  index: number;
}) {
  const form = useShippingMethodForm();
  const settings = useSettings();
  const baseCurrency = settings.baseCurrency;

  return (
    <>
      <form.Field name={`bands[${index}].requirement`}>
        {(field) => (
          <Dropdown
            label={t('features:shipping_methods.components.band_requirement.label')}
            options={shippingBandRequirementSchema.options.map((option) => ({
              label: t(
                `features:shipping_methods.components.band_requirement.options.${option.shape.type.value}`,
              ),
              value: option.shape.type.value,
            }))}
            // Falls back to '' because value becomes `undefined` momentarily when deleting band
            value={field.state.value?.type ?? ''}
            // Set default values to show in form fields for selected requirement type
            onChange={(value) =>
              field.handleChange(
                getDefaultRequirementState(value as ShippingBandApiResponse['requirement']['type']),
              )
            }
          />
        )}
      </form.Field>

      {/* User can define min and max totals for this shipping band to apply */}
      <form.Subscribe selector={(state) => state.values.bands[index]?.requirement?.type}>
        {(requirementType) =>
          requirementType === 'net_total' && (
            <>
              <span className="flex items-stretch justify-stretch gap-2">
                <span className="flex-1">
                  <form.Field name={`bands[${index}].requirement.minTotal`}>
                    {(field) => (
                      <Input
                        inputMode="numeric"
                        label={t(
                          'features:shipping_methods.components.band_requirement.net_total.from.label',
                        )}
                        placeholder={t(
                          'features:shipping_methods.components.band_requirement.net_total.from.placeholder',
                        )}
                        suffix={baseCurrency}
                        value={field.state.value}
                        onChange={field.handleChange}
                        error={field.state.meta.errors[0]?.message}
                      />
                    )}
                  </form.Field>
                </span>

                <span className="flex-1">
                  <form.Field name={`bands[${index}].requirement.maxTotal`}>
                    {(field) => (
                      <Input
                        inputMode="numeric"
                        label={t(
                          'features:shipping_methods.components.band_requirement.net_total.to.label',
                        )}
                        placeholder={t(
                          'features:shipping_methods.components.band_requirement.net_total.to.placeholder',
                        )}
                        suffix={baseCurrency}
                        value={field.state.value}
                        onChange={field.handleChange}
                        error={field.state.meta.errors[0]?.message}
                      />
                    )}
                  </form.Field>
                </span>
              </span>

              <Text variant="secondary" size="sm">
                {/* Trans component used here to support <strong> */}
                <Trans
                  ns="features"
                  i18nKey="shipping_methods.components.band_requirement.net_total.info"
                />
              </Text>
            </>
          )
        }
      </form.Subscribe>

      {/* User can define min and max weight for this shipping band to apply */}
      <form.Subscribe selector={(state) => state.values.bands[index]?.requirement?.type}>
        {(requirementType) =>
          requirementType === 'weight' && (
            <>
              <span className="flex items-stretch justify-stretch gap-2">
                <span className="flex-1">
                  <form.Field name={`bands[${index}].requirement.minGrams`}>
                    {(field) => (
                      <Input
                        inputMode="numeric"
                        label={t(
                          'features:shipping_methods.components.band_requirement.weight.from.label',
                        )}
                        placeholder={t(
                          'features:shipping_methods.components.band_requirement.weight.from.placeholder',
                        )}
                        value={field.state.value}
                        onChange={field.handleChange}
                        error={field.state.meta.errors[0]?.message}
                      />
                    )}
                  </form.Field>
                </span>

                <span className="flex-1">
                  <form.Field name={`bands[${index}].requirement.maxGrams`}>
                    {(field) => (
                      <Input
                        inputMode="numeric"
                        label={t(
                          'features:shipping_methods.components.band_requirement.weight.to.label',
                        )}
                        placeholder={t(
                          'features:shipping_methods.components.band_requirement.weight.to.placeholder',
                        )}
                        value={field.state.value}
                        onChange={field.handleChange}
                        error={field.state.meta.errors[0]?.message}
                      />
                    )}
                  </form.Field>
                </span>
              </span>

              <Text variant="secondary" size="sm">
                {/* Trans component used here to support <strong> */}
                <Trans
                  ns="features"
                  i18nKey="shipping_methods.components.band_requirement.weight.info"
                />
              </Text>
            </>
          )
        }
      </form.Subscribe>

      <form.Subscribe selector={(state) => state.values.bands[index]?.requirement?.type}>
        {(requirementType) =>
          requirementType === 'none' && (
            <Text variant="secondary" size="sm">
              {t('features:shipping_methods.components.band_requirement.none.info')}
            </Text>
          )
        }
      </form.Subscribe>
    </>
  );
}
